import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Tooltip } from 'primeng/tooltip';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';

export interface TimelineReservation {
  id: string;
  patientId: string;
  patientName: string;
  bedId: string;
  checkInDate: Date;
  checkOutDate: Date;
  packageDays: 7 | 10 | 14 | 35;
  status:
    | 'tentative'
    | 'half_confirmed'
    | 'booked'
    | 'checked_in'
    | 'checked_out'
    | 'no_show'
    | 'canceled'
    | 'archived'
    | 'reserved'
    | 'confirmed'
    | 'in_progress'
    | 'completed'
    | 'blocked'
    | 'extension';
  treatmentDay?: number; // Día en que va al cuarto de tratamiento
}

export interface MockedPlan {
  id: string;
  bedId: string;
  startDate: Date;
  packageDays: number;
  planName: string;
  isMocked: true; // Marca que es un plan mockeado, no una reservación real
  programId?: string; // ID del programa
  programName?: string; // Nombre del programa para mostrar
  totalAmount?: number; // Monto total del paquete
}

export interface RoomBed {
  room_id?: string;
  room_number: string;
  room_type?: string;
}

interface TimelineDay {
  date: Date;
  dayNumber: number;
  isWeekend: boolean;
  isToday: boolean;
}

interface BedRow {
  bedId: string;
  bedName: string;
  reservations: TimelineReservation[];
  mockedPlans: MockedPlan[];
}

@Component({
  selector: 'lib-reservations-timeline',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, Tooltip, Select],
  templateUrl: './reservations-timeline.component.html',
  styleUrls: ['./reservations-timeline.component.css'],
})
export class ReservationsTimelineComponent implements OnInit, OnChanges {
  @Input() reservations: TimelineReservation[] = [];
  @Input() mockedPlans: MockedPlan[] = [];
  @Input() totalBeds = 21;
  @Input() rooms: RoomBed[] = []; // Lista de habitaciones del backend
  @Input() daysToShow = 0; // 0 = infinite scroll
  @Output() reservationClick = new EventEmitter<TimelineReservation>();
  @Output() cellClick = new EventEmitter<{ bedId: string; date: Date }>();
  @Output() planClick = new EventEmitter<MockedPlan>();

  startDate = new Date();
  days: TimelineDay[] = [];
  beds: BedRow[] = [];
  zoomLevel = 25; // Default zoom level (25%)

  daysOptions = [
    { label: 'reservations.timeline.daysOptions.infinite', value: 0 },
    { label: 'reservations.timeline.daysOptions.60days', value: 60 },
    { label: 'reservations.timeline.daysOptions.90days', value: 90 },
    { label: 'reservations.timeline.daysOptions.180days', value: 180 },
  ];

  zoomOptions = [
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
  ];

  // Mapa de colores según estado (siguiendo buenas prácticas de UX)
  statusColors: Record<string, string> = {
    // Estados del backend
    tentative: '#fde047', // yellow-300 - Tentativa (amarillo: pendiente/provisional)
    half_confirmed: '#fbbf24', // yellow-400 - Medio Confirmada (amarillo oscuro: en proceso)
    booked: '#86efac', // green-300 - Reservada (verde claro: confirmación parcial)
    checked_in: '#93c5fd', // blue-300 - Registrado (azul: activo/en progreso)
    checked_out: '#c4b5fd', // purple-300 - Finalizado (morado: completado)
    no_show: '#fb923c', // orange-400 - No Se Presentó (naranja: advertencia)
    canceled: '#fca5a5', // red-300 - Cancelada (rojo: cancelado/error)
    archived: '#d1d5db', // gray-300 - Archivada (gris: inactivo/archivado)

    // Estados legacy (por compatibilidad)
    reserved: '#fde047', // yellow-300 - Igual a tentative
    confirmed: '#86efac', // green-300 - Igual a booked
    in_progress: '#93c5fd', // blue-300 - Igual a checked_in
    completed: '#c4b5fd', // purple-300 - Igual a checked_out
    cancelled: '#fca5a5', // red-300 - Igual a canceled
    blocked: '#ef4444', // red-500 - Bloqueada (rojo oscuro: bloqueado)
    extension: '#a78bfa', // purple-400 - Extensión (morado más oscuro: extensión)
  };

  // Días de tratamiento según paquete
  treatmentDayMap: Record<number, number> = {
    7: 4,
    10: 4,
    14: 8,
    35: 28,
  };

  ngOnInit(): void {
    this.generateTimeline();
    setTimeout(() => this.scrollToToday(), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reservations'] || changes['mockedPlans']) {
      this.updateBeds();
    }
  }

  updateBeds(): void {
    this.beds = [];

    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach((room) => {
        const bedId = room.room_id || `ROOM-${room.room_number}`;
        const bedReservations = this.reservations.filter((r) => r.bedId === bedId);
        const bedPlans = this.mockedPlans.filter((p) => p.bedId === bedId);

        this.beds.push({
          bedId,
          bedName: room.room_number,
          reservations: bedReservations,
          mockedPlans: bedPlans,
        });
      });
    } else {
      for (let i = 1; i <= this.totalBeds; i++) {
        const bedId = `BED-${i.toString().padStart(2, '0')}`;
        this.beds.push({
          bedId,
          bedName: `Cama ${i}`,
          reservations: this.reservations.filter((r) => r.bedId === bedId),
          mockedPlans: this.mockedPlans.filter((p) => p.bedId === bedId),
        });
      }
    }
  }

  generateTimeline(): void {
    // Generar días
    this.days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let daysBack: number;
    let daysForward: number;

    if (this.daysToShow === 0) {
      daysBack = 90; // 90 días hacia atrás
      daysForward = 180; // 180 días hacia adelante
    } else {
      daysBack = Math.floor(this.daysToShow / 3);
      daysForward = this.daysToShow - daysBack;
    }

    for (let i = daysBack; i > 0; i--) {
      const date = new Date(this.startDate);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      this.days.push({
        date,
        dayNumber: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: false,
      });
    }

    for (let i = 0; i < daysForward; i++) {
      const date = new Date(this.startDate);
      date.setDate(date.getDate() + i);
      date.setHours(0, 0, 0, 0);

      this.days.push({
        date,
        dayNumber: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.getTime() === today.getTime(),
      });
    }

    this.updateBeds();
  }

  scrollToToday(): void {
    const todayIndex = this.days.findIndex((day) => day.isToday);
    if (todayIndex === -1) return;

    const scrollContainer = document.querySelector('.days-scroll-container') as HTMLElement;
    if (!scrollContainer) return;

    const cellWidth = this.getCellWidth();
    const scrollPosition = todayIndex * cellWidth - scrollContainer.clientWidth / 2;

    scrollContainer.scrollLeft = Math.max(0, scrollPosition);
  }

  loadMoreDays(): void {
    if (this.daysToShow !== 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastDay = this.days[this.days.length - 1];
    const tenYearsFromNow = new Date(today);
    tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);

    if (lastDay.date >= tenYearsFromNow) {
      return;
    }

    const daysToAdd = 90; // Cargar 90 días más cada vez

    for (let i = 0; i < daysToAdd; i++) {
      const date = new Date(lastDay.date);
      date.setDate(date.getDate() + i + 1);
      date.setHours(0, 0, 0, 0);

      if (date > tenYearsFromNow) break;

      this.days.push({
        date,
        dayNumber: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.getTime() === today.getTime(),
      });
    }
  }

  loadMorePastDays(): void {
    if (this.daysToShow !== 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDay = this.days[0];
    const tenYearsAgo = new Date(today);
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);

    if (firstDay.date <= tenYearsAgo) {
      return;
    }

    const scrollContainer = document.querySelector('.days-scroll-container') as HTMLElement;
    if (!scrollContainer) return;

    const previousScrollLeft = scrollContainer.scrollLeft;
    const cellWidth = this.getCellWidth();
    const daysToAdd = 90; // Cargar 90 días más cada vez
    const newDays: TimelineDay[] = [];

    for (let i = daysToAdd; i > 0; i--) {
      const date = new Date(firstDay.date);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      if (date < tenYearsAgo) continue;

      newDays.push({
        date,
        dayNumber: date.getDate(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isToday: date.getTime() === today.getTime(),
      });
    }

    this.days = [...newDays, ...this.days];

    setTimeout(() => {
      scrollContainer.scrollLeft = previousScrollLeft + newDays.length * cellWidth;
    }, 0);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollLeft = element.scrollLeft;
    const scrollPosition = scrollLeft + element.clientWidth;
    const scrollWidth = element.scrollWidth;

    if (scrollPosition >= scrollWidth * 0.9) {
      this.loadMoreDays();
    }

    if (scrollLeft <= scrollWidth * 0.1 && scrollLeft < 500) {
      this.loadMorePastDays();
    }

    if (scrollLeft <= scrollWidth * 0.1 && scrollLeft < 500) {
      this.loadMorePastDays();
    }
  }

  getCellWidth(): number {
    const baseWidth = 120; // px
    return (baseWidth * this.zoomLevel) / 100;
  }

  onZoomChange(): void {
    // El cambio de zoom se refleja automáticamente en los estilos
  }

  getReservationStyle(reservation: TimelineReservation): Record<string, string | number> {
    const checkIn = this.parseLocalDate(reservation.checkInDate);

    // Calcular posición inicial (columna)
    const startDayIndex = this.days.findIndex((day) => day.date.getTime() === checkIn.getTime());

    if (startDayIndex === -1) return { display: 'none' };

    // Duración exacta según el paquete (no calculada por fechas)
    const duration = reservation.packageDays;

    const cellWidth = this.getCellWidth(); // Ancho dinámico según zoom
    const left = startDayIndex * cellWidth;
    const width = duration * cellWidth - 4; // -4px para el gap

    // Convertir color hex a rgba con opacidad 0.6
    const color = this.statusColors[reservation.status] || '#d1d5db';
    const rgbaColor = this.hexToRgba(color, 0.6);

    return {
      left: `${left}px`,
      width: `${width}px`,
      backgroundColor: rgbaColor,
    };
  }

  hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getTreatmentDayPosition(reservation: TimelineReservation): number | null {
    const treatmentDay = this.treatmentDayMap[reservation.packageDays];
    if (!treatmentDay) return null;

    const checkIn = this.parseLocalDate(reservation.checkInDate);

    const treatmentDate = new Date(checkIn);
    treatmentDate.setDate(treatmentDate.getDate() + treatmentDay - 1);

    const startDayIndex = this.days.findIndex((day) => day.date.getTime() === checkIn.getTime());

    if (startDayIndex === -1) return null;

    return startDayIndex + treatmentDay - 1;
  }

  shouldShowTreatmentMarker(reservation: TimelineReservation, dayIndex: number): boolean {
    const treatmentPosition = this.getTreatmentDayPosition(reservation);
    return treatmentPosition === dayIndex;
  }

  onReservationClick(reservation: TimelineReservation, event: Event): void {
    event.stopPropagation();
    this.reservationClick.emit(reservation);
  }

  onCellClick(bedId: string, date: Date): void {
    this.cellClick.emit({ bedId, date });
  }

  previousPeriod(): void {
    this.startDate.setDate(this.startDate.getDate() - this.daysToShow);
    this.generateTimeline();
  }

  nextPeriod(): void {
    this.startDate.setDate(this.startDate.getDate() + this.daysToShow);
    this.generateTimeline();
  }

  today(): void {
    this.startDate = new Date();
    this.startDate.setHours(0, 0, 0, 0);
    this.generateTimeline();
  }

  onDaysChange(): void {
    this.generateTimeline();
  }

  getMonthLabel(dayIndex: number): string | null {
    if (dayIndex === 0) {
      const date = this.days[0].date;
      return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }

    const prevDay = this.days[dayIndex - 1];
    const currentDay = this.days[dayIndex];

    if (prevDay.date.getMonth() !== currentDay.date.getMonth()) {
      return currentDay.date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }

    return null;
  }

  hasReservationOnDay(bed: BedRow, dayIndex: number): boolean {
    const day = this.parseLocalDate(this.days[dayIndex].date);

    return bed.reservations.some((reservation) => {
      const checkIn = this.parseLocalDate(reservation.checkInDate);
      const checkOut = this.parseLocalDate(reservation.checkOutDate);

      return day >= checkIn && day <= checkOut;
    });
  }

  getMockedPlanStyle(plan: MockedPlan): Record<string, string | number> {
    const startDate = this.parseLocalDate(plan.startDate);

    // Calcular posición inicial
    const startDayIndex = this.days.findIndex((day) => day.date.getTime() === startDate.getTime());

    if (startDayIndex === -1) return { display: 'none' };

    const duration = plan.packageDays;
    const cellWidth = this.getCellWidth();
    const left = startDayIndex * cellWidth;
    const width = duration * cellWidth - 4;

    // Color distintivo para planes mockeados (azul semi-transparente con borde punteado)
    return {
      left: `${left}px`,
      width: `${width}px`,
      backgroundColor: 'rgba(59, 130, 246, 0.15)',
      border: '2px dashed #3b82f6',
    };
  }

  onPlanClick(plan: MockedPlan, event: Event): void {
    event.stopPropagation();
    this.planClick.emit(plan);
  }

  private parseLocalDate(date: string | Date): Date {
    if (date instanceof Date) {
      // Si ya es Date, crear nueva instancia en hora local
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}
